/**
 * Mini chart components for observation cards
 * Lightweight visualizations that provide at-a-glance insights
 */
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { cn } from '@/lib/utils';

// Color palette for charts
const COLORS = {
  primary: 'hsl(var(--primary))',
  good: 'hsl(var(--status-good))',
  warning: 'hsl(var(--status-warning))',
  critical: 'hsl(var(--status-critical))',
  muted: 'hsl(var(--muted))',
  chart1: 'hsl(var(--chart-1))',
  chart2: 'hsl(var(--chart-2))',
  chart3: 'hsl(var(--chart-3))',
  chart4: 'hsl(var(--chart-4))',
  chart5: 'hsl(var(--chart-5))',
};

const DONUT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#6b7280', // gray (other)
];

interface MiniDonutChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  size?: number;
  innerRadius?: number;
  className?: string;
}

/**
 * Mini donut chart for showing allocation breakdown
 * Used in diversification and sector exposure cards
 */
export function MiniDonutChart({
  data,
  size = 80,
  innerRadius = 20,
  className
}: MiniDonutChartProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className={cn("flex items-center justify-center bg-muted/30 rounded-full", className)}
        style={{ width: size, height: size }}
      >
        <span className="text-xs text-muted-foreground">No data</span>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={size / 2 - 2}
            paddingAngle={2}
            dataKey="value"
            strokeWidth={0}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color || DONUT_COLORS[index % DONUT_COLORS.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

interface MiniSparklineProps {
  data: number[];
  color?: string;
  height?: number;
  className?: string;
  showArea?: boolean;
  negativeColor?: string;
}

/**
 * Mini sparkline for showing trends
 * Used in drawdown and performance cards
 */
export function MiniSparkline({
  data,
  color = COLORS.primary,
  height = 40,
  className,
  showArea = true,
  negativeColor = COLORS.critical,
}: MiniSparklineProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className={cn("flex items-center justify-center bg-muted/30 rounded", className)}
        style={{ height }}
      >
        <span className="text-xs text-muted-foreground">No data</span>
      </div>
    );
  }

  const chartData = data.map((value, index) => ({ index, value }));
  const minValue = Math.min(...data);
  const hasNegative = minValue < 0;

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id="sparklineGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={hasNegative ? negativeColor : color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={hasNegative ? negativeColor : color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={hasNegative ? negativeColor : color}
            strokeWidth={1.5}
            fill={showArea ? "url(#sparklineGradient)" : "none"}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

interface MiniBarChartProps {
  data: Array<{ name: string; value: number; benchmark?: number }>;
  height?: number;
  className?: string;
  showBenchmark?: boolean;
  valueColor?: string;
  benchmarkColor?: string;
}

/**
 * Mini bar chart for comparisons
 * Used in fee comparison and performance cards
 */
export function MiniBarChart({
  data,
  height = 60,
  className,
  showBenchmark = false,
  valueColor = COLORS.primary,
  benchmarkColor = COLORS.muted,
}: MiniBarChartProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className={cn("flex items-center justify-center bg-muted/30 rounded", className)}
        style={{ height }}
      >
        <span className="text-xs text-muted-foreground">No data</span>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 40 }}>
          <XAxis type="number" hide />
          <Tooltip
            formatter={(value: number) => [`${(value * 100).toFixed(2)}%`, '']}
            contentStyle={{
              backgroundColor: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              fontSize: '12px',
            }}
          />
          {showBenchmark && (
            <Bar dataKey="benchmark" fill={benchmarkColor} radius={[0, 2, 2, 0]} barSize={8} />
          )}
          <Bar dataKey="value" fill={valueColor} radius={[0, 4, 4, 0]} barSize={12} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface HorizontalCompareBarProps {
  yourValue: number;
  benchmarkValue: number;
  label?: string;
  yourLabel?: string;
  benchmarkLabel?: string;
  format?: 'percent' | 'currency' | 'number';
  className?: string;
}

/**
 * Horizontal comparison bar showing your value vs benchmark
 */
export function HorizontalCompareBar({
  yourValue,
  benchmarkValue,
  label,
  yourLabel = 'You',
  benchmarkLabel = 'Benchmark',
  format = 'percent',
  className,
}: HorizontalCompareBarProps) {
  const maxValue = Math.max(yourValue, benchmarkValue);
  const yourWidth = maxValue > 0 ? (yourValue / maxValue) * 100 : 0;
  const benchmarkWidth = maxValue > 0 ? (benchmarkValue / maxValue) * 100 : 0;

  const formatValue = (val: number) => {
    if (format === 'percent') return `${(val * 100).toFixed(2)}%`;
    if (format === 'currency') return `$${val.toLocaleString()}`;
    return val.toFixed(2);
  };

  const isYourBetter = format === 'percent'
    ? yourValue < benchmarkValue // Lower fees are better
    : yourValue > benchmarkValue; // Higher returns are better

  return (
    <div className={cn("space-y-2", className)}>
      {label && <p className="text-xs font-medium text-muted-foreground">{label}</p>}

      <div className="space-y-1.5">
        {/* Your value */}
        <div className="flex items-center gap-2">
          <span className="text-xs w-16 text-muted-foreground">{yourLabel}</span>
          <div className="flex-1 h-3 bg-muted/30 rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                isYourBetter ? "bg-status-good" : "bg-primary"
              )}
              style={{ width: `${yourWidth}%` }}
            />
          </div>
          <span className={cn(
            "text-xs font-mono w-16 text-right",
            isYourBetter ? "text-status-good" : ""
          )}>
            {formatValue(yourValue)}
          </span>
        </div>

        {/* Benchmark value */}
        <div className="flex items-center gap-2">
          <span className="text-xs w-16 text-muted-foreground">{benchmarkLabel}</span>
          <div className="flex-1 h-3 bg-muted/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-muted-foreground/40 rounded-full"
              style={{ width: `${benchmarkWidth}%` }}
            />
          </div>
          <span className="text-xs font-mono w-16 text-right text-muted-foreground">
            {formatValue(benchmarkValue)}
          </span>
        </div>
      </div>
    </div>
  );
}

interface SectorBreakdownProps {
  sectors: Array<{ name: string; weight: number }>;
  className?: string;
}

/**
 * Sector breakdown visualization with mini pie and legend
 */
export function SectorBreakdown({ sectors, className }: SectorBreakdownProps) {
  if (!sectors || sectors.length === 0) {
    return (
      <div className={cn("text-center text-xs text-muted-foreground py-4", className)}>
        No sector data available
      </div>
    );
  }

  // Sort by weight and take top 5, group rest as "Other"
  const sorted = [...sectors].sort((a, b) => b.weight - a.weight);
  const top5 = sorted.slice(0, 5);
  const otherWeight = sorted.slice(5).reduce((sum, s) => sum + s.weight, 0);

  const chartData = [
    ...top5.map(s => ({ name: s.name, value: s.weight })),
    ...(otherWeight > 0 ? [{ name: 'Other', value: otherWeight }] : []),
  ];

  return (
    <div className={cn("flex items-center gap-4", className)}>
      <MiniDonutChart data={chartData} size={70} innerRadius={18} />
      <div className="flex-1 space-y-1">
        {chartData.slice(0, 4).map((sector, i) => (
          <div key={sector.name} className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }}
            />
            <span className="text-xs flex-1 truncate">{sector.name}</span>
            <span className="text-xs font-mono text-muted-foreground">
              {(sector.value * 100).toFixed(0)}%
            </span>
          </div>
        ))}
        {chartData.length > 4 && (
          <span className="text-xs text-muted-foreground">
            +{chartData.length - 4} more
          </span>
        )}
      </div>
    </div>
  );
}

interface CrisisScenario {
  name: string;
  portfolioImpact: number; // e.g., -0.35 for 35% loss
  spImpact: number;        // S&P 500 impact for comparison
}

interface StressTestChartProps {
  currentValue: number;
  scenarios: CrisisScenario[];
  protectionFactor?: number; // e.g., 0.6 means 40% loss reduction
  className?: string;
}

/**
 * Stacked line chart showing portfolio value trajectory during a market crisis
 * Compares unprotected vs protected outcomes over time
 */
export function StressTestChart({
  currentValue,
  scenarios,
  protectionFactor = 0.6,
  className,
}: StressTestChartProps) {
  const formatCurrency = (val: number) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val.toFixed(0)}`;
  };

  // Calculate average impact from real scenarios
  const avgImpact = scenarios.length > 0
    ? scenarios.reduce((sum, s) => sum + Math.abs(s.portfolioImpact), 0) / scenarios.length
    : 0.35;

  // Build crisis trajectory - simulates a market crash and recovery
  const bottomValue = currentValue * (1 - avgImpact);
  const protectedBottomValue = currentValue * (1 - avgImpact * protectionFactor);
  const savings = protectedBottomValue - bottomValue;

  // Generate smooth curve data points for the crisis trajectory
  const chartData = [
    { month: 'Start', unprotected: currentValue, protected: currentValue },
    { month: '3mo', unprotected: currentValue * 0.94, protected: currentValue * 0.97 },
    { month: '6mo', unprotected: currentValue * 0.82, protected: currentValue * 0.91 },
    { month: '9mo', unprotected: bottomValue, protected: protectedBottomValue },
    { month: '12mo', unprotected: bottomValue * 1.06, protected: protectedBottomValue * 1.04 },
    { month: '18mo', unprotected: bottomValue * 1.15, protected: protectedBottomValue * 1.10 },
  ];

  return (
    <div className={cn("space-y-2", className)}>
      {/* Stacked area line chart */}
      <div className="h-[140px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 10 }}>
            <defs>
              <linearGradient id="unprotectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="protectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(v) => formatCurrency(v)}
              axisLine={false}
              tickLine={false}
              width={45}
              domain={['dataMin - 10000', 'dataMax + 10000']}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '11px',
                padding: '8px 12px',
              }}
              formatter={(value: number, name: string) => [
                formatCurrency(value),
                name === 'unprotected' ? 'Without Protection' : 'With Protection'
              ]}
            />
            {/* Unprotected area (red) - rendered first so protected overlays */}
            <Area
              type="monotone"
              dataKey="unprotected"
              stroke="#ef4444"
              strokeWidth={2.5}
              fill="url(#unprotectedGradient)"
              dot={false}
              activeDot={{ r: 4, fill: '#ef4444' }}
            />
            {/* Protected area (green) - rendered on top */}
            <Area
              type="monotone"
              dataKey="protected"
              stroke="#10b981"
              strokeWidth={2.5}
              fill="url(#protectedGradient)"
              dot={false}
              activeDot={{ r: 4, fill: '#10b981' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-5 text-[10px]">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-0.5 bg-[#ef4444] rounded" />
          <span className="text-muted-foreground">Without Protection</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-0.5 bg-[#10b981] rounded" />
          <span className="text-muted-foreground">With Protection</span>
        </div>
      </div>

      {/* Savings callout */}
      {savings > 0 && (
        <div className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-status-good/10 border border-status-good/20">
          <span className="text-xs text-muted-foreground">Protection saves</span>
          <span className="text-sm font-bold text-status-good">{formatCurrency(savings)}</span>
          <span className="text-xs text-muted-foreground">at crisis bottom</span>
        </div>
      )}
    </div>
  );
}

interface SharpeComparisonProps {
  yourSharpe: number;
  benchmarkSharpe?: number;
  potentialSharpe?: number;
  className?: string;
}

/**
 * Sharpe ratio comparison visualization
 * Shows current efficiency vs market average with honest improvement potential
 */
export function SharpeComparison({
  yourSharpe,
  benchmarkSharpe = 0.5,
  potentialSharpe,
  className,
}: SharpeComparisonProps) {
  // Sharpe ratio scale: 0 to 1.5 for visualization
  const scaleMax = 1.5;

  const getBarWidth = (val: number) => {
    return Math.max(0, Math.min(100, (val / scaleMax) * 100));
  };

  const getSharpeColor = (val: number, benchmark: number) => {
    if (val >= benchmark) return '#10b981'; // At or above benchmark
    if (val >= benchmark * 0.7) return '#f59e0b'; // Within 30% of benchmark
    return '#ef4444'; // Significantly below
  };

  const getSharpeLabel = (val: number, benchmark: number) => {
    if (val >= benchmark) return 'On Target';
    if (val >= benchmark * 0.7) return 'Close';
    return 'Below Target';
  };

  // Calculate improvement metrics
  const hasImprovement = potentialSharpe && potentialSharpe > yourSharpe;
  const improvementAmount = hasImprovement ? potentialSharpe - yourSharpe : 0;
  const wouldReachBenchmark = potentialSharpe && potentialSharpe >= benchmarkSharpe;
  const gapToBenchmark = benchmarkSharpe - yourSharpe;
  const gapAfterOptimization = potentialSharpe ? benchmarkSharpe - potentialSharpe : gapToBenchmark;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Your Sharpe */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Your Return Efficiency</span>
          <div className="flex items-center gap-1.5">
            <span className="font-mono font-medium" style={{ color: getSharpeColor(yourSharpe, benchmarkSharpe) }}>
              {yourSharpe.toFixed(2)}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{
              backgroundColor: `${getSharpeColor(yourSharpe, benchmarkSharpe)}20`,
              color: getSharpeColor(yourSharpe, benchmarkSharpe)
            }}>
              {getSharpeLabel(yourSharpe, benchmarkSharpe)}
            </span>
          </div>
        </div>
        <div className="h-3 bg-muted/30 rounded-full overflow-hidden relative">
          {/* Benchmark marker */}
          <div
            className="absolute h-full w-0.5 bg-muted-foreground z-10"
            style={{ left: `${getBarWidth(benchmarkSharpe)}%` }}
          />
          {/* Your bar */}
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${getBarWidth(yourSharpe)}%`,
              backgroundColor: getSharpeColor(yourSharpe, benchmarkSharpe),
            }}
          />
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground">
          <span>0</span>
          <span className="relative" style={{ left: `${getBarWidth(benchmarkSharpe) - 50}%` }}>
            Target: {benchmarkSharpe.toFixed(2)}
          </span>
          <span>1.5</span>
        </div>
      </div>

      {/* Gap analysis */}
      {gapToBenchmark > 0 && (
        <div className="p-2 rounded-md bg-muted/20 border border-border/50">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Gap to target:</span>
            <span className="font-mono text-status-warning">-{gapToBenchmark.toFixed(2)}</span>
          </div>

          {hasImprovement && (
            <div className="mt-2 pt-2 border-t border-border/30">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">With optimization:</span>
                <span className="font-mono text-status-good">{potentialSharpe?.toFixed(2)}</span>
              </div>
              {wouldReachBenchmark ? (
                <p className="text-[10px] text-status-good mt-1">
                  Reaches target with fee reduction & rebalancing
                </p>
              ) : (
                <p className="text-[10px] text-muted-foreground mt-1">
                  +{improvementAmount.toFixed(2)} improvement possible (gap remains: {gapAfterOptimization.toFixed(2)})
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Already at or above benchmark */}
      {gapToBenchmark <= 0 && (
        <div className="p-2 rounded-md bg-status-good/10 border border-status-good/20">
          <p className="text-xs text-status-good">
            Your portfolio is efficient for the risk taken
          </p>
        </div>
      )}
    </div>
  );
}

interface PotentialSavingsProps {
  currentFees: number;
  optimizedFees: number;
  years?: number;
  portfolioValue: number;
  className?: string;
}

/**
 * Potential savings visualization for fee optimization
 */
export function PotentialSavings({
  currentFees,
  optimizedFees,
  years = 10,
  portfolioValue,
  className,
}: PotentialSavingsProps) {
  const annualSavings = (currentFees - optimizedFees) * portfolioValue;
  const totalSavings = annualSavings * years; // Simplified, not compounded
  const savingsPercent = currentFees > 0 ? ((currentFees - optimizedFees) / currentFees) * 100 : 0;

  if (annualSavings <= 0) {
    return (
      <div className={cn("text-center py-2", className)}>
        <p className="text-sm text-status-good">Your fees are already optimized!</p>
      </div>
    );
  }

  return (
    <div className={cn("p-3 rounded-lg bg-status-good/10 border border-status-good/20", className)}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Potential Savings</span>
        <span className="text-xs text-muted-foreground">{years}-year projection</span>
      </div>

      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-status-good">
          ${totalSavings >= 1000 ? `${(totalSavings / 1000).toFixed(0)}K` : totalSavings.toFixed(0)}
        </span>
        <span className="text-sm text-muted-foreground">
          ({savingsPercent.toFixed(0)}% reduction)
        </span>
      </div>

      <div className="mt-2 text-xs text-muted-foreground">
        ${annualSavings.toFixed(0)}/year by switching to lower-cost alternatives
      </div>
    </div>
  );
}
